import React from 'react';
import { CardComponentRegistry as ICardComponentRegistry, CardSectionProps, OperationalMode, Environment } from '../../../types/compositeCards';
import { CardHeader, CardContent, CardActions, CardMetadata } from '../BaseComponents';
import { cardComponentRegistry } from '../ComponentRegistry/CardComponentRegistry';

class CardRegistry {
  private registry: ICardComponentRegistry = {
    baseComponents: {
      header: CardHeader as React.ComponentType<CardSectionProps>,
      content: CardContent as React.ComponentType<CardSectionProps>,
      actions: CardActions as React.ComponentType<CardSectionProps>,
      metadata: CardMetadata as React.ComponentType<CardSectionProps>,
    },
    roleComponents: {
      architect: {
        header: CardHeader as React.ComponentType<CardSectionProps>,
        content: CardContent,
        actions: CardActions,
        metadata: CardMetadata,
      },
      operator: {
        header: CardHeader as React.ComponentType<CardSectionProps>,
        content: CardContent,
        actions: CardActions,
        metadata: CardMetadata,
      },
      coach: {
        header: CardHeader as React.ComponentType<CardSectionProps>,
        content: CardContent,
        actions: CardActions,
        metadata: CardMetadata,
      },
      integrator: {
        header: CardHeader as React.ComponentType<CardSectionProps>,
        content: CardContent,
        actions: CardActions,
        metadata: CardMetadata,
      },
      development: {
        header: CardHeader as React.ComponentType<CardSectionProps>,
        content: CardContent,
        actions: CardActions,
        metadata: CardMetadata,
      },
      marketfit: {
        header: CardHeader as React.ComponentType<CardSectionProps>,
        content: CardContent,
        actions: CardActions,
        metadata: CardMetadata,
      },
    },
    modelComponents: {
      Prime: {
        header: CardHeader as React.ComponentType<CardSectionProps>,
        content: CardContent,
        actions: CardActions,
        metadata: CardMetadata,
      },
      Company: {
        header: CardHeader as React.ComponentType<CardSectionProps>,
        content: CardContent,
        actions: CardActions,
        metadata: CardMetadata,
      },
      Sandbox: {
        header: CardHeader as React.ComponentType<CardSectionProps>,
        content: CardContent,
        actions: CardActions,
        metadata: CardMetadata,
      },
    },
    cardTypeComponents: {},
  };

  constructor() {
    // Merge the imported cardComponentRegistry components into this registry
    this.registry = {
      baseComponents: { ...this.registry.baseComponents, ...cardComponentRegistry.baseComponents },
      roleComponents: { ...this.registry.roleComponents, ...cardComponentRegistry.roleComponents },
      modelComponents: { ...this.registry.modelComponents, ...cardComponentRegistry.modelComponents },
      cardTypeComponents: { ...this.registry.cardTypeComponents, ...cardComponentRegistry.cardTypeComponents },
    };
  }

  /**
   * Register a component for a specific section type
   */
  registerComponent(
    sectionType: string,
    component: React.ComponentType<any>,
    role?: OperationalMode,
    model?: Environment
  ): void {
    if (role && model) {
      // Role and model specific component
      if (!this.registry.modelComponents[model][sectionType]) {
        this.registry.modelComponents[model][sectionType] = component;
      }
      if (!this.registry.roleComponents[role][sectionType]) {
        this.registry.roleComponents[role][sectionType] = component;
      }
    } else if (role) {
      // Role specific component
      this.registry.roleComponents[role][sectionType] = component;
    } else if (model) {
      // Model specific component
      this.registry.modelComponents[model][sectionType] = component;
    } else {
      // Base component
      this.registry.baseComponents[sectionType] = component;
    }
  }

  /**
   * Get a component for a specific card type, section type, role, and model
   */
  getComponent(
    cardType: string,
    sectionType: string,
    role?: OperationalMode,
    model?: Environment
  ): React.ComponentType<any> | null {
    // 1. Check for card-type-specific component
    if (this.registry.cardTypeComponents[cardType]?.[sectionType]) {
      return this.registry.cardTypeComponents[cardType][sectionType];
    }

    // 2. Check for role-specific component
    if (role && this.registry.roleComponents[role]?.[sectionType]) {
      return this.registry.roleComponents[role][sectionType];
    }

    // 3. Check for model-specific component
    if (model && this.registry.modelComponents[model]?.[sectionType]) {
      return this.registry.modelComponents[model][sectionType];
    }

    // 4. Fallback to base component
    return this.registry.baseComponents[sectionType] || null;
  }

  /**
   * Get all available components for a role and model combination
   */
  getAvailableComponents(role: OperationalMode, model: Environment): Record<string, React.ComponentType<any>> {
    const components: Record<string, React.ComponentType<any>> = {};

    // Get base components
    Object.entries(this.registry.baseComponents).forEach(([key, component]) => {
      components[key] = component;
    });

    // Override with role-specific components
    Object.entries(this.registry.roleComponents[role]).forEach(([key, component]) => {
      if (component) {
        components[key] = component;
      }
    });

    // Override with model-specific components
    Object.entries(this.registry.modelComponents[model]).forEach(([key, component]) => {
      if (component) {
        components[key] = component;
      }
    });

    return components;
  }

  /**
   * Get the registry instance
   */
  getRegistry(): ICardComponentRegistry {
    return this.registry;
  }

  /**
   * Clear all registered components
   */
  clearRegistry(): void {
    this.registry = {
      baseComponents: {},
      roleComponents: {
        architect: {},
        operator: {},
        coach: {},
        integrator: {},
        development: {},
        marketfit: {},
      },
    modelComponents: {
      Prime: {},
      Company: {},
      Sandbox: {},
    },
    cardTypeComponents: {},
    };
  }

  /**
   * Check if a component is registered
   */
  isComponentRegistered(
    cardType: string,
    sectionType: string,
    role?: OperationalMode,
    model?: Environment
  ): boolean {
    return this.getComponent(cardType, sectionType, role, model) !== null;
  }

  /**
   * Get all registered section types
   */
  getRegisteredSectionTypes(): string[] {
    const types = new Set<string>();

    // Add base component types
    Object.keys(this.registry.baseComponents).forEach(type => types.add(type));

    // Add role component types
    Object.values(this.registry.roleComponents).forEach(roleComponents => {
      Object.keys(roleComponents).forEach(type => types.add(type));
    });

    // Add model component types
    Object.values(this.registry.modelComponents).forEach(modelComponents => {
      Object.keys(modelComponents).forEach(type => types.add(type));
    });

    return Array.from(types);
  }
}

// Export the new comprehensive registry
export const cardRegistry = cardComponentRegistry;
export default cardRegistry;
